import Phaser from 'phaser';
import { Unit, UnitConfig } from './Unit';
import { PhaseBehavior, PhaseConfig } from '../behaviors/PhaseBehavior';

export interface BossPhase {
  hpPercent: number;
  pattern: string;
  dropOnEnter?: string;
  [key: string]: unknown;
}

export interface BossConfig extends UnitConfig {
  phases?: BossPhase[];
  activationRange?: number;
  displayName?: string;
  displayNameKey?: string;
  portrait?: string;
  behaviors?: {
    phases?: PhaseConfig;
    [key: string]: unknown;
  };
}

/**
 * Boss - Unit with phase-based attack patterns
 *
 * Used for: RastaFarmer, CyberScareCrow, etc.
 *
 * Features:
 * - Has HealthBehavior (from Unit)
 * - Has PhaseBehavior for phase transitions
 * - Changes attack pattern based on HP
 * - Can drop items on phase change
 * - Has activation range
 */
export class Boss extends Unit {
  public phases: PhaseBehavior;
  public isBoss: boolean = true;
  public activationRange: number;
  public isActivated: boolean = false;
  public displayName: string;
  public displayNameKey?: string;
  public portrait: string | undefined;
  private healthBarVisible: boolean = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: BossConfig = {}
  ) {
    super(scene, x, y, texture, config);

    // Bosses have phase behavior
    this.phases = this.addBehavior(new PhaseBehavior(this, {
      phases: config.phases || []
    }));

    this.activationRange = config.activationRange ?? 500;
    this.displayName = config.displayName ?? texture;
    this.displayNameKey = config.displayNameKey;
    this.portrait = config.portrait;
    console.log(`[Boss] constructor - texture: "${texture}", displayName: "${this.displayName}", portrait: "${this.portrait}"`);
  }

  // Debug: track if we've logged update once
  private _hasLoggedUpdate = false;

  /**
   * Update - check activation and phase transitions
   */
  update(time: number, delta: number): void {
    super.update(time, delta);

    // Log once to confirm update is being called
    if (!this._hasLoggedUpdate) {
      console.log(`[Boss] update() is being called for "${this.displayName}"`);
      this._hasLoggedUpdate = true;
    }

    // Check for activation if not yet activated
    if (!this.isActivated) {
      this.checkActivation();
    }

    // Check for phase transitions based on HP
    if (this.isActivated) {
      this.phases.checkTransition(this.hp, this.maxHp);

      // Re-show health bar if player returns to range and bar is hidden
      if (!this.healthBarVisible && !this.isDead()) {
        this.checkHealthBarVisibility();
      }
    }
  }

  /**
   * Check if player is in range and show health bar if hidden
   */
  private checkHealthBarVisibility(): void {
    const player = this.scene.registry.get('player') as Phaser.GameObjects.Sprite | undefined;
    if (!player) return;

    if (this.isPlayerInRange(player.x, player.y)) {
      this.showHealthBar();
    }
  }

  /**
   * Show the boss health bar
   */
  showHealthBar(): void {
    if (this.healthBarVisible) return;

    this.healthBarVisible = true;
    console.log(`[Boss] Showing health bar for "${this.displayName}"`);
    this.scene.events.emit('showBossBar', { boss: this, name: this.displayName, nameKey: this.displayNameKey, portrait: this.portrait });
  }

  /**
   * Called when health bar is hidden (by BossHealthBar)
   */
  onHealthBarHidden(): void {
    this.healthBarVisible = false;
    console.log(`[Boss] Health bar hidden for "${this.displayName}"`);
  }

  /**
   * Check if player is in range and activate
   */
  private checkActivation(): void {
    const player = this.scene.registry.get('player') as Phaser.GameObjects.Sprite | undefined;
    if (!player) {
      console.log('[Boss] checkActivation - no player found in registry');
      return;
    }

    const distance = Phaser.Math.Distance.Between(this.x, this.y, player.x, player.y);

    if (this.isPlayerInRange(player.x, player.y)) {
      console.log(`[Boss] Player in range! Distance: ${Math.round(distance)}, activationRange: ${this.activationRange}`);
      this.activate();
    }
  }

  /**
   * Activate the boss (when player enters range)
   */
  activate(): void {
    if (this.isActivated) return;

    this.isActivated = true;
    console.log(`[Boss] activate() - displayName: "${this.displayName}", HP: ${this.hp}/${this.maxHp}`);
    this.onActivate();

    // Emit activation event
    this.scene.events.emit('bossActivated', { boss: this });

    // Show health bar
    this.showHealthBar();
  }

  /**
   * Override in subclasses for activation effects
   */
  onActivate(): void {
    // Play activation animation, show boss bar, etc.
  }

  /**
   * Get current phase
   */
  get currentPhase(): BossPhase | undefined {
    return this.phases.currentPhase;
  }

  /**
   * Get current attack pattern
   */
  get currentPattern(): string {
    return this.currentPhase?.pattern || 'default';
  }

  /**
   * Override in subclasses for phase change handling
   */
  onPhaseChange(newPhase: BossPhase, oldPhase: BossPhase | undefined): void {
    // Change animation, speed, behavior, etc.
  }

  /**
   * Override for boss death (special handling)
   */
  onDeath(): void {
    super.onDeath();

    // Emit boss defeated event
    this.scene.events.emit('bossDefeated', { boss: this });
  }

  /**
   * Override for boss death handling
   */
  handleDeath(): void {
    // Bosses usually have longer death sequences
    // Override in specific boss classes
  }

  /**
   * Check if player is in activation range
   */
  isPlayerInRange(playerX: number, playerY: number): boolean {
    const distance = Phaser.Math.Distance.Between(
      this.x, this.y,
      playerX, playerY
    );
    return distance <= this.activationRange;
  }
}
